/**
 * T492 — Helper that classifies an expense's OCR-pipeline state into a
 * "failure severity" signal the UI can render as a banner / row-marker.
 *
 * Three return states:
 *   - 'failed'          → pipeline crashed (expense_processing_status.phase === 'failed').
 *   - 'low-confidence'  → pipeline finished but the average OCR confidence
 *                         landed below 0.40, AND the row hasn't been
 *                         eRechnung-validated.
 *   - null              → either trusted (validated), no signal yet, or
 *                         OK enough to leave to the per-field badges.
 *
 * Splits the two states so the banner wording can differ: 'failed' =
 * "OCR-Erkennung fehlgeschlagen — alle Felder manuell prüfen",
 * 'low-confidence' = "OCR-Erkennung unsicher — bitte Werte prüfen".
 *
 * Pure / side-effect-free so it can be unit-tested without React.
 * The `-` prefix on the filename excludes both this helper and its
 * sibling test from TanStack Router's auto-route scan.
 */

export type OcrFailureSeverity = 'failed' | 'low-confidence';

export interface OcrFailureSeverityInput {
  processingPhase: string | null | undefined;
  trustLevel: 'validated' | 'review' | 'manual';
  source: string;
  attachments: ReadonlyArray<{
    isPrimary?: boolean;
    isErechnungXml?: boolean;
    ocrFieldConfidences?: Record<string, number> | null;
  }>;
}

const LOW_CONFIDENCE_THRESHOLD = 0.4;

export function getOcrFailureSeverity(
  input: OcrFailureSeverityInput
): OcrFailureSeverity | null {
  // Pipeline crashed beats every other signal — show the strongest banner.
  if (input.processingPhase === 'failed') return 'failed';

  // eRechnung-validated rows are trusted; never flag.
  if (input.trustLevel === 'validated' || input.source === 'erechnung') return null;

  // Low-confidence only applies to the OCR / PDF-text branch (trustLevel='review').
  if (input.trustLevel !== 'review') return null;

  // Compute avg over the primary attachment's confidence map (falls back to
  // the first attachment with confidences). Mirrors the heuristic used by
  // OcrConfidenceBadge so banner + badges stay aligned.
  const primary =
    input.attachments.find((a) => a.isPrimary) ??
    input.attachments.find((a) => a.ocrFieldConfidences && Object.keys(a.ocrFieldConfidences).length > 0) ??
    input.attachments[0];
  const fc = primary?.ocrFieldConfidences;
  if (!fc || Object.keys(fc).length === 0) return null;
  const values = Object.values(fc).filter((v) => typeof v === 'number');
  if (values.length === 0) return null;
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  // OCR pipelines sometimes return 0..1 floats, sometimes 0..100. Normalise.
  const norm = avg > 1 ? avg / 100 : avg;
  return norm < LOW_CONFIDENCE_THRESHOLD ? 'low-confidence' : null;
}
